import * as React from 'react';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, ITicketingState, TicketingActionCreators, ITicketLookupDataResponse, ICannedResponseResponse, IAppState } from '@nkr/store/index';
import { ITicketCannedResponseRequest } from '@nkr/models/index';
import { CannedResponseAvailability } from '@nkr/enums/index';
import { Button, FormGroup, ControlLabel, FormControl, HelpBlock } from 'react-bootstrap';
import { Icon } from 'react-icons-kit';
import { circleThin } from 'react-icons-kit/fa/circleThin';
import { dotCircleO } from 'react-icons-kit/fa/dotCircleO';
import JoditEditor from '@nkr/components/editor';
import { toast } from 'react-toastify';
import config from '../jodit-editor-config';

interface IProps {
    id: number
}

interface IStateProps {
    ticketing: ITicketingState,
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getCannedResponse: (id: number) => Promise<ICannedResponseResponse>,
    saveCannedResponse: (request: ITicketCannedResponseRequest) => Promise<ICannedResponseResponse>,
    getLookupData: () => Promise<ITicketLookupDataResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

interface IState {
    id: number,
    title: string,
    message: string,
    availability: CannedResponseAvailability,
    valid: {
        title: boolean,
        message: boolean
    },
    dirty: boolean
}

class TicketCannedAddEdit extends React.Component<TProps, IState> {
    readonly state: IState = {
        id: 0,
        title: '',
        message: '',
        availability: CannedResponseAvailability.Myself,
        valid: {
            title: true,
            message: true
        },
        dirty: false
    }

    componentDidMount = () => {
        this.getData();
    }

    getData = () => {
        this.props.showLoading('Loading canned response, one moment please...').then(() => {
            return Promise.all([
                this.props.getCannedResponse(this.props.id),
                this.props.getLookupData()
            ]).then((response: [ICannedResponseResponse, ITicketLookupDataResponse]) => {
                let response1 = response[0];

                if (response1.data.success && response1.data.results) {
                    let { id, title, message, availability } = response1.data.results;

                    this.setState({
                        id,
                        title,
                        message,
                        availability
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    handleTitleChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        this.setState({
            title: e.currentTarget.value
        });
    }

    handleMessageChange = (message: string) => {
        this.setState({
            message
        });
    }

    handleAvailabilityChange = (availability: CannedResponseAvailability) => {
        this.setState({
            availability
        });
    }

    handleSave = () => {
        let { id, title, message, availability } = this.state;

        let valid = {
            title: title != '',
            message: message != ''
        }

        let isValid = valid.title && valid.message;

        this.setState({
            valid,
            dirty: true
        }, () => {
            if (isValid) {
                let request: ITicketCannedResponseRequest = {
                    id,
                    title,
                    message,
                    availability,
                    ownerId: this.props.app.user.id
                };

                this.props.showLoading('Saving canned response, one moment please...').then(() => {
                    this.props.saveCannedResponse(request).then(response => {
                        this.props.hideLoading();

                        if (response.data.success) {
                            toast.success('Canned response saved successfully', {
                                onClose: () => {
                                    window.location.href = '/ticketing/canned-responses';
                                }
                            })
                        }
                    });
                });
            }
        })
    }

    handleCancel = () => {
        window.location.href = '/ticketing/canned-responses';
    }

    render = () => {
        return (
            <div className='container'>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h1 style={{ marginTop: 0, marginBottom: 20 }}>Add Canned Response</h1>
                </div>

                <FormGroup validationState={this.state.valid.title ? null : 'error'}>
                    <ControlLabel>Response Title</ControlLabel>
                    <FormControl
                        value={this.state.title}
                        onChange={this.handleTitleChange}
                    />
                    {!this.state.valid.title && this.state.dirty && (
                        <HelpBlock>Please specify the canned response title</HelpBlock>
                    )}
                </FormGroup>

                <FormGroup validationState={this.state.valid.message ? null : 'error'}>
                    <ControlLabel>Message</ControlLabel>
                    <JoditEditor
                        value={this.state.message}
                        config={config}
                        onChange={this.handleMessageChange}
                        onBlur={() => null}
                    />
                    {!this.state.valid.message && this.state.dirty && (
                        <HelpBlock>Please specify the canned response message</HelpBlock>
                    )}
                </FormGroup>

                <FormGroup style={{ display: 'flex' }}>
                    <label style={{ marginRight: 20 }}>Available for</label>
                    <div>
                        <div style={{ marginBottom: 10, cursor: 'pointer' }} onClick={() => this.handleAvailabilityChange(1)}>
                            <span>
                                <Icon icon={this.state.availability == CannedResponseAvailability.Myself ? dotCircleO : circleThin} style={{ marginRight: 5 }} />Myself
                            </span>
                        </div>
                        <div style={{ cursor: 'pointer' }} onClick={() => this.handleAvailabilityChange(2)}>
                            <span>
                                <Icon icon={this.state.availability == CannedResponseAvailability.AllAgents ? dotCircleO : circleThin} style={{ marginRight: 5 }} />All Agents
                            </span>
                        </div>
                    </div>
                </FormGroup>

                <div className='text-right'>
                    <Button bsStyle='primary' style={{ width: 100, marginRight: 10 }} onClick={this.handleSave}>Save</Button>
                    <Button style={{ width: 100 }} onClick={this.handleCancel}>Cancel</Button>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        ticketing: state.ticketing,
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getCannedResponse: (id) => {
            return dispatch(TicketingActionCreators.getCannedResponse(id));
        },
        saveCannedResponse: (request) => {
            return dispatch(TicketingActionCreators.saveCannedResponse(request));
        },
        getLookupData: () => {
            return dispatch(TicketingActionCreators.getLookupData());
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(TicketCannedAddEdit);